import { Modal, notification } from "antd";
import cuid from "cuid";
import GeneralCosts from "entities/GeneralCosts";
import { find, get, filter } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import edit from "shared/assets/icons/generate.svg";
import { useLazyQuery, useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import {
  GENERATE_QUOTE_DOCUMENTS,
  GENERATE_ZIP_DOCUMENT,
} from "utils/api/graphql/mutations/attachment";
import { BUILD_GENERAL_COSTS } from "utils/api/graphql/mutations/costs";
import { CREATE_SUBSCRIPTION } from "utils/api/graphql/mutations/subscription";
import { SEND_COMPLETE_SUBSCRIPTION } from "utils/api/graphql/mutations/webhook";
import { TRACER } from "utils/api/graphql/queries/tracers";
import {
  CONTACT_POINT_SYSTEM,
  INSURANCE_PLAN_ATTACHMENTS_TYPES,
  SUR_COMPLEMENT_WEIGHT,
  THEMES_PALETTE,
} from "utils/constants";
import { removeDuplicates } from "utils/helpers/array";
import { v4 } from "uuid";
import View from "./View";

const Quote = ({ project, onNext, onBack }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PROJECT);
  const DETAIL = t("DETAIL", { returnObjects: true });
  const [addSubscription] = useMutation(CREATE_SUBSCRIPTION);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generateQuoteDocuments] = useMutation(GENERATE_QUOTE_DOCUMENTS);
  const [generateZipDocuments] = useMutation(GENERATE_ZIP_DOCUMENT);
  const [attachments, setAttachments] = useState([
    {
      name: DETAIL.TEMPLATES.QUOTE.QUOTE_FILE,
      contentType: "application/pdf",
    },
    {
      name: DETAIL.TEMPLATES.QUOTE.DUTY_FILE,
      contentType: "application/pdf",
    },
  ]);

  const selectedPlans =
    get(project, "fields.lastSelected", [])?.length > 0
      ? get(project, "fields.lastSelected", [])
      : get(project, "fields.insurancePlans", []);

  const { loading } = useQuery(graphql.queries.ATTACHMENTS, {
    variables: {
      isIn: { type: Object.values(INSURANCE_PLAN_ATTACHMENTS_TYPES) },
      where:
        selectedPlans.length > 0
          ? {
              OR: selectedPlans.map((id) => ({
                AND: [{ insurancePlans: { id } }, { project: null }],
              })),
            }
          : { id: null },
    },
    onCompleted: (data) => {
      const uniqAttachments = removeDuplicates(
        get(data, "attachments.data", []),
      );
      setAttachments((prev) => [...prev, ...uniqAttachments]);
    },
  });

  const [addLink] = useMutation(graphql.mutations.CREATE_LINK);
  const [getTracer] = useLazyQuery(TRACER);
  const [buildGeneralCosts] = useMutation(BUILD_GENERAL_COSTS);
  const [sendCompleteSubscription] = useMutation(SEND_COMPLETE_SUBSCRIPTION);

  const [updateContact] = useMutation(graphql.mutations.UPDATE_CONTACT, {
    onCompleted: async (response, { variables }) => {
      const {
        data: { tracer: tracerData },
      } = await getTracer({
        variables: {
          where: {
            flow: {
              process: {
                theme: THEMES_PALETTE.B2B_LMF.PROSPECT_PROCESS,
              },
            },
          },
        },
      });

      const statuses = get(tracerData, "flow.process.processStatuses", []);

      const selectedData = get(project, "fields.lastSelected", []);
      const insurancePlans = selectedData.map((id) => ({ id }));

      const updatedTelecoms = get(variables, "data.telecoms", []);
      const emailTelecom = updatedTelecoms.find(
        (telecom) => telecom.system === CONTACT_POINT_SYSTEM.EMAIL,
      );
      const email = emailTelecom?.value;

      const firstName = get(project, "contact.user.firstname");
      const lastName = get(project, "contact.user.lastname");

      if (!email || !firstName || !lastName) {
        notification.error({
          message: "Impossible d'envoyer le webhook",
          description: `Données manquantes : ${!firstName ? "prénom, " : ""}${
            !lastName ? "nom, " : ""
          }${!email ? "email" : ""}`,
        });
        return;
      }

      try {
        const { errors } = await generateQuoteDocuments({
          variables: {
            data: {
              project: { id: project.id },
              insurancePlans,
            },
          },
        });

        if (errors) {
          setIsSubmitting(false);
          throw new Error(`GraphQL Error: ${JSON.stringify(errors)}`);
        }

        const { data: buildResult } = await buildGeneralCosts({
          variables: {
            data: {
              project: { id: project.id },
              insurancePlanFilter: {
                isIn: {
                  id: selectedData,
                },
              },
            },
          },
        });

        const generalCostsData = get(buildResult, "buildGeneralCosts", []);
        let links = {};

        const createSubscription = async (insurancePlanId) => {
          const { data: subscriptionData } = await addSubscription({
            variables: {
              data: {
                id: cuid(),
                project: { id: project.id },
                insurancePlan: { id: insurancePlanId },
              },
            },
          });

          const selectedStatus = find(statuses, {
            template: "EFFECTIVE_DATE",
          })?.id;

          const url = get(
            subscriptionData,
            "addSubscription.insurancePlan.config.generatedUrl",
            `${process.env.REACT_APP_BASE_URL}/#/public/process/init/${get(
              tracerData,
              "source.id",
            )}`,
          );

          const { data: linkData } = await addLink({
            variables: {
              data: {
                url,
                subscription: { id: subscriptionData.addSubscription.id },
                project: { id: project.id },
                fields: {
                  process: { id: get(tracerData, "flow.process.id") },
                  status: { id: selectedStatus },
                },
              },
            },
          });

          links[insurancePlanId] = `${url}?l=${linkData.addLink.id}`;
        };

        await Promise.all(selectedData.map(createSubscription));
        const products = await Promise.all(
          generalCostsData.map(async (generalCost) => {
            const insurancePlan = get(generalCost, "insurancePlan");
            const cost = get(generalCost, "cost");
            const insurancePlanId = get(insurancePlan, "id");
            const url = links[insurancePlanId];

            const selectedUpsells = get(
              project,
              `fields.upsells.${insurancePlanId}`,
              [],
            );
            const surComplementPlanRefs = filter(selectedUpsells, {
              weight: SUR_COMPLEMENT_WEIGHT,
            }).map(({ insurancePlan: { id } }) => id);

            const basePrice = cost || 0;
            const upsellsPrice = selectedUpsells.reduce(
              (acc, { cost }) => acc + cost,
              0,
            );
            const totalPrice = basePrice + upsellsPrice;

            const mainPlanName = get(insurancePlan, "name");
            const selectedModulesText =
              selectedUpsells.length > 0
                ? ` + ${selectedUpsells
                    .map(({ insurancePlan }) => insurancePlan.name)
                    .join(", ")}`
                : "";
            const fullLabel = `${mainPlanName}${selectedModulesText}`;

            const {
              data: {
                generateZipDocuments: { fileUrl: documentsUrl },
              },
            } = await generateZipDocuments({
              variables: {
                where: {
                  OR: [
                    ...surComplementPlanRefs.map((id) => ({
                      AND: [{ insurancePlans: { id } }, { project: null }],
                    })),
                    {
                      AND: [
                        { insurancePlans: { id: insurancePlanId } },
                        { project: null },
                      ],
                    },
                    {
                      AND: [
                        {
                          OR: insurancePlans.map(({ id }) => ({
                            insurancePlans: { id },
                          })),
                        },
                        { OR: [{ project: { id: project.id } }] },
                      ],
                    },
                  ],
                },
              },
            });

            const product = {
              label: fullLabel,
              price: totalPrice ? `${totalPrice.toFixed(2)}€/mois` : "",
              url: url,
              documentsUrl,
            };

            return product;
          }),
        );

        const notificationData = {
          recipient: email,
          externalId: v4(),
          language: "FR",
          data: {
            lastName: lastName,
            firstName: firstName,
            products: products,
            agentFirstName: get(project, "author.firstname", "Camille"),
            agentEmail: get(
              project,
              "author.email",
              "camille@mutuelle-familiale.fr",
            ),
          },
        };

        await sendCompleteSubscription({
          variables: {
            notification: notificationData,
          },
          onCompleted: () => {
            setIsSubmitting(false);
            notification.open({
              message: DETAIL.TEMPLATES.QUOTE.NOTIFICATION.SUCCESS,
              duration: 5,
              type: "success",
            });
            onNext({
              payload: {
                locked: true,
                fields: {
                  ...project.fields,
                  insurancePlans: [
                    ...get(project, "fields.insurancePlans", []),
                    ...get(project, "fields.lastSelected", []),
                  ],
                },
              },
            });
          },
          onError: (err) => {
            setIsSubmitting(false);
            notification.error({
              message: "Erreur webhook Engage",
              description: err.message,
            });
          },
        });
      } catch (error) {
        setIsSubmitting(false);
        notification.error({
          message: "Erreur webhook Engage",
          description: error.message,
        });
      }
    },
  });

  const sendQuote = (telecoms) => {
    setIsSubmitting(true);
    updateContact({
      variables: {
        where: {
          id: project.contact.id,
        },
        data: {
          telecoms: [
            ...project.contact.telecoms.filter(
              (telecom) => telecom.system !== CONTACT_POINT_SYSTEM.EMAIL,
            ),
            ...telecoms.map(({ label, value }) => ({
              id: value,
              system: CONTACT_POINT_SYSTEM.EMAIL,
              value: label,
            })),
          ],
        },
      },
    });
  };

  const generalCostsFilter = {
    where: {
      project: {
        id: project.id,
      },
    },
    isIn: {
      insurancePlan: {
        id: get(project, "fields.lastSelected", []),
      },
    },
  };

  return (
    <>
      {isSubmitting && (
        <Modal open width={"60%"} footer={null}>
          <div className="modal-info">
            <img src={edit} alt={"en cours"} />
            <div className="modal-info__text">
              <h4>{DETAIL.TEMPLATES.QUOTE.IN_PROGRESS}</h4>
              <p>{DETAIL.TEMPLATES.QUOTE.GENERATING_DOCUMENTS}</p>
              <p>{DETAIL.TEMPLATES.QUOTE.FEW_SECONDS}</p>
            </div>
          </div>
        </Modal>
      )}
      <GeneralCosts
        filter={generalCostsFilter}
        loading={loading}
        updateContact={updateContact}
        attachments={attachments}
        setAttachments={setAttachments}
        project={project}
        onNext={sendQuote}
        onBack={onBack}
        View={View}
        NoData={View}
        isSubmitting={isSubmitting}
      />
    </>
  );
};

export default Quote;
